import { CommunicationKey } from '@models/Communication'
import { TestUser } from '@models/TestUser'

/**
 * Whether a communication goes out as a push notification.
 *
 * The key prefix is the discriminator: the enum carries no channel field, and
 * the modal receives nothing else that names the channel.
 */
export const isPushCommunication = (key: CommunicationKey): boolean =>
  key.startsWith('notification_')

/**
 * How a test recipient reads in the recipient list.
 *
 * An account shows its name, a free address shows the address itself — the same
 * value the received email substitutes for the recipient name variables.
 */
export const testRecipientLabel = (recipient: TestUser): string =>
  recipient.user !== null
    ? `${recipient.user.firstName} ${recipient.user.lastName}`
    : (recipient.email ?? '')

/**
 * Whether an account backs this recipient.
 *
 * The push channel addresses a device through a user, so a free address is not
 * a candidate there.
 */
export const isUserBackedRecipient = (recipient: TestUser): boolean =>
  recipient.user !== null

/**
 * The copy behind each error key the API returns.
 *
 * The API answers with raw keys and never translates them, so the wording lives
 * here — the single place to touch when a key is added.
 */
const TEST_RECIPIENT_ERRORS: Record<string, string> = {
  'communication.test_users.already_registered':
    'This address is already in the recipient list.',
  'communication.send_test.push_requires_user':
    'A push test only reaches a recipient linked to a user account.',
  'communication.send_test.no_reachable_device':
    'This user has no registered device to push to.',
}

/**
 * The message for an error key, or the fallback when the key is unknown.
 *
 * The fallback is passed in rather than imported: this module is pulled by the
 * entity registry, which `App` itself imports.
 */
export const testRecipientErrorMessage = (
  key: string | undefined,
  fallback: string
): string => (key !== undefined && TEST_RECIPIENT_ERRORS[key]) || fallback

/**
 * What `SBAPI` rejects with, narrowed to what these endpoints answer.
 *
 * `message` carries the key of a toolkit status response; a validation failure
 * only fills `errors`, either bare (Laravel) or wrapped in `data` (toolkit).
 */
export interface TestRecipientRejection {
  message?: string
  error?: {
    response?: {
      data?: {
        errors?: Record<string, string[]>
        data?: { errors?: Record<string, string[]> }
      }
    }
  }
}

/**
 * Why registering a test recipient failed.
 *
 * A duplicate comes back as a status key, a malformed address as a validation
 * error on the `email` field.
 */
export const recipientCreationError = (
  rejection: unknown,
  fallback: string
): string => {
  const { message, error } = (rejection ?? {}) as TestRecipientRejection
  const validation = error?.response?.data?.errors?.email?.[0]

  return validation ?? testRecipientErrorMessage(message, fallback)
}

export interface TestSendFailure {
  recipientId: number
  message: string
}

/**
 * Which recipients a test send could not reach.
 *
 * The API keys each failure on the position the front submitted, not on the
 * recipient id, so the submitted order is what maps an error back to its row.
 * An empty result means the failure was not a per-recipient one.
 */
export const sendTestFailures = (
  rejection: unknown,
  submittedIds: number[],
  fallback: string
): TestSendFailure[] => {
  const { error } = (rejection ?? {}) as TestRecipientRejection
  const errors = error?.response?.data?.data?.errors

  if (errors === undefined) return []

  return Object.entries(errors).flatMap(([field, messages]) => {
    const recipientId = submittedIds[Number(field.split('.')[1])]

    return recipientId === undefined
      ? []
      : [
          {
            recipientId,
            message: testRecipientErrorMessage(messages[0], fallback),
          },
        ]
  })
}
